// Centralized Spanish UI copy for the chat shell.
//
// Spanish surface, English code (repo convention). Keep user-facing strings here
// so terminology stays consistent with the agent's domain vocabulary
// (segmento / gama / marca / canal / capas) and so a future i18n layer has a
// single source. Do not inline Spanish strings in components.

export const copy = {
  app: {
    title: "AskMe · Asistente de Promotores OCESA",
  },
  login: {
    title: "Asistente de Promotores",
    subtitle: "Inicia sesión para analizar artistas, eventos y ventas.",
    signIn: "Iniciar sesión",
    footer: "Acceso restringido — uso interno OCESA.",
  },
  models: {
    // aria-label for the model-selector dropdown in the header.
    selectLabel: "Seleccionar modelo",
  },
  effort: {
    // aria-label for the reasoning-effort dropdown in the header.
    selectLabel: "Seleccionar nivel de esfuerzo",
  },
  chat: {
    // Relative-time prefix for a message sent yesterday, e.g. "Ayer, 14:32".
    // A dedicated chat label (not copy.sidebar.groupYesterday) because the
    // message timestamp and the sidebar date-group heading are distinct
    // surfaces that could diverge in wording later. The comma/space separator
    // is composed in code, so this stays a bare, reusable word.
    yesterdayPrefix: "Ayer",
    // Label for the per-response wall-clock turn duration (request → completion).
    elapsedPrefix: "Tiempo transcurrido",
  },
  actions: {
    // Copy-to-clipboard control on an assistant response.
    copy: "Copiar respuesta",
    copied: "Copiado",
    // Copiar y calificar quedan inertes mientras el turno transmite: la respuesta
    // todavía crece, así que copiarla daría un texto a medias y calificarla mandaría
    // al feedback algo que no es lo que el promotor acabó leyendo. La barra sigue
    // visible —esconderla movería el layout a media respuesta— pero deshabilitada, y
    // el título dice por qué en vez de dejar un botón muerto sin explicación.
    pendingTurn: "Disponible cuando termine la respuesta",
  },
  header: {
    newChat: "Nuevo chat",
    logout: "Cerrar sesión",
    logoutConfirmTitle: "Confirmar cierre de sesión",
    logoutConfirmDescription:
      "¿Seguro que quieres cerrar sesión? Tendrás que iniciar sesión de nuevo para acceder a tu cuenta.",
    cancel: "Cancelar",
    confirm: "Confirmar",
  },
  welcome: {
    greeting: "Bienvenido a",
    title: "AskMe",
    subtitle: "Pregúntame sobre artistas, audiencias, eventos y ventas de boletos para tus decisiones de programación.",
    suggestionsLabel: "Algunas sugerencias para ti…",
  },
  sidebar: {
    history: "Historial",
    toggle: "Mostrar u ocultar el panel",
    // Close control shown inside the sidebar on mobile (where it is full-screen).
    close: "Cerrar el panel",
    // Empty state shown when the user has no saved conversations yet.
    empty: "Aún no tienes conversaciones guardadas.",
    // Fallback title for a conversation with no usable first prompt.
    untitled: "Sin título",
    // Date-group headings for the session list (most-recent-first).
    groupToday: "Hoy",
    groupYesterday: "Ayer",
    groupOlder: "Anteriores",
    // Per-item actions and their ARIA labels.
    delete: "Eliminar",
    deleteAria: "Eliminar conversación",
    openAria: "Abrir conversación",
    rename: "Cambiar nombre",
    renameAria: "Cambiar nombre de la conversación",
    // Accessible label for the per-row streaming spinner.
    streamingAria: "Generando respuesta…",
    // Delete confirmation dialog.
    deleteConfirmTitle: "Eliminar conversación",
    deleteConfirmDescription:
      "¿Seguro que quieres eliminar esta conversación? Esta acción no se puede deshacer.",
    deleteCancel: "Cancelar",
    deleteConfirm: "Eliminar",
  },
  // Starter prompts for the empty state — example questions in the agent's
  // domain (artistas, audiencias, eventos, ventas de boletos). A random subset
  // rotates on each load (see ChatInterface) so returning users see variety.
  suggestions: [
    "¿Qué tan popular es Bad Bunny en México? dame sus oyentes mensuales de Spotify",
    "¿Qué evento tuvo más ventas el año pasado? dame el top 5 con boletos vendidos",
    "Compara la fanbase de BTS con otros artistas de K-pop en México",
    "¿Cuál es el perfil demográfico de la audiencia de Peso Pluma en Guadalajara?",
    "¿Va a vender bien un concierto de Karol G en el Estadio GNP?",
    "Dame los artistas más escuchados en Monterrey en Spotify",
    "Compara las ventas de boletos de 2025 vs 2024 por trimestre",
    "¿Qué géneros musicales son más populares en la CDMX?",
    "Recomienda artistas de Regional Mexicano para un cartel en Guadalajara",
    "¿Cuánto ingreso generó el Electric Daisy Carnival el año pasado?",
    "Sugiere un acto invitado para un show de Luis Miguel",
    "¿Cuál es la población y el ingreso promedio por edad en Puebla?",
    "Top 10 de eventos por ingresos en el Estadio GNP Seguros",
    "¿Cómo se comparan las ventas de eventos de K-pop vs pop latino?",
    "Dame los seguidores en redes de los 3 artistas más grandes de banda",
    "¿Qué canales de venta generan más ingresos por evento?",
    "Analiza la viabilidad de un evento de música electrónica en el Autódromo",
    "¿Qué artistas internacionales tienen más oyentes mensuales en México?",
  ],
  input: {
    placeholder: "Pregúntame lo que quieras…",
    send: "Enviar",
    sending: "Pensando…",
    stop: "Detener",
    // Shown on hover over the clip control; states the attachment limits.
    attach: "Adjuntar archivos — hasta 5 archivos, 3.75 MB c/u (imágenes, PDF, Word, Excel, CSV, texto)",
    // Inline notices when a picked file is rejected client-side.
    attachTooLarge: "Algunos archivos superan el límite de 3.75 MB y se omitieron.",
    attachTooMany: "Solo se permiten 5 archivos por mensaje; se omitieron los adicionales.",
    // El turno no cabe en el cuerpo que acepta API Gateway (límite medido:
    // 10,485,760 bytes). Solo las imágenes cuentan: viajan como base64 EN el cuerpo e
    // inflan 4/3, mientras los documentos van por S3 y solo mandan su referencia. Con
    // el tope de 3.75 MB por archivo, 3 imágenes al tope ya se pasan.
    //
    // El aviso va ANTES de codificar y subir: el 413 de API Gateway se aplica antes del
    // authorizer, así que si el cuerpo sale ya no hay forma de dar un mensaje decente —
    // el usuario esperaba toda la codificación para recibir un
    // `HTTP 413: HTTP content length exceeded 10485760 bytes` en inglés crudo.
    // Un archivo por encima del techo de su tipo. Se nombra el archivo porque con
    // varios adjuntos «uno pesa demasiado» obliga al usuario a adivinar cuál.
    attachFileTooLarge: (nombres: readonly string[], maxMB: number) =>
      nombres.length === 1
        ? `«${nombres[0]}» pesa demasiado. El máximo por archivo es ${maxMB} MB: redúcelo e inténtalo de nuevo.`
        : `Estos archivos pesan demasiado: ${nombres
            .map(n => `«${n}»`)
            .join(", ")}. El máximo por archivo es ${maxMB} MB.`,

    // El texto del turno es lo único que todavía viaja en el cuerpo, y CloudFront
    // bloquea cualquier cuerpo de más de 8 KB (`SizeRestrictions_BODY`). Sin este
    // aviso el usuario recibe un 403 de HTML que no se parece a un problema de
    // tamaño. Se pide un recorte concreto en vez de sólo decir que no cabe.
    promptTooLong: (excesoBytes: number) =>
      `Este mensaje es demasiado largo para enviarse completo. Recorta alrededor de ${Math.ceil(
        excesoBytes / 100
      ) * 100} caracteres, o adjunta el texto como archivo.`,
  },
  status: {
    thinking: "Pensando…",
    reasoningHint: "Esto podría tardar unos segundos...",
    reasoningTitle: "Razonamiento",
    stopped: "Generación detenida.",
    // ADR-DOC-06. Con adjuntos el turno tarda 3–5 min y "Pensando…" no dice que la
    // espera es por el archivo; el diseño lo llama necesario.
    //
    // La etiqueta nombra el TIPO y la CANTIDAD de lo adjuntado. El defecto observado
    // en dev era llamar «documento» a una imagen (un JPG mostraba "Procesando
    // documento…"). Que la imagen viaje en el cuerpo del turno y el documento pase
    // por S3 es un detalle de implementación que el usuario no ve: en ambos casos
    // sus bytes salen de su máquina, así que "Subiendo…" es correcto para los dos.
    //
    // Reglas (`initialStatusLabel`):
    //   1 imagen                    → "Analizando imagen…"
    //   2+ imágenes                 → "Analizando imágenes…"
    //   1 documento y nada más      → "Analizando documento…"
    //   ≥1 documento + algo más     → "Analizando documentos…"
    //
    // El documento manda en las mezclas, y el plural sigue al TOTAL DE ARCHIVOS, no al
    // conteo de documentos: con un PDF y un JPG adjuntos el usuario ve dos insignias y
    // espera por dos archivos, así que el singular se leería como si uno se hubiera
    // ignorado — exactamente lo que esta tarea existe para evitar.
    //
    // «Analizando» y no «Subiendo»: describe lo que el usuario quiere que pase con su
    // archivo, no el mecanismo de transporte — que además difiere por carril (S3 vs
    // cuerpo del turno) y le es invisible.
    analyzingImage: "Analizando imagen…",
    analyzingImages: "Analizando imágenes…",
    analyzingDocument: "Analizando documento…",
    analyzingDocuments: "Analizando documentos…",
    // Segunda fase, SOLO para documentos: los bytes ya están en S3 y ahora el runtime
    // los está leyendo. El plural sigue la misma regla — se observó "Procesando
    // documento…" en singular con un PDF y un JPG adjuntos.
    // Las imágenes no tienen segunda fase (viajan en el turno), así que se quedan en
    // "Analizando…" hasta el primer byte.
    processingDocument: "Procesando documento…",
    processingDocuments: "Procesando documentos…",
  },
  feedback: {
    good: "Buena respuesta",
    bad: "Respuesta deficiente",
    positiveAria: "Marcar como respuesta útil",
    negativeAria: "Marcar como respuesta deficiente",
    thanks: "¡Gracias por tu comentario!",
    dialogTitlePositive: "Comentario positivo",
    dialogTitleNegative: "Comentario negativo",
    dialogDescription: "Cuéntanos más sobre tu experiencia (opcional)",
    dialogPlaceholder: "Comparte tus comentarios…",
    cancel: "Cancelar",
    send: "Enviar",
    sending: "Enviando…",
  },
  preferences: {
    menuItem: "Preferencias",
    title: "Preferencias",
    description:
      "Ajustan los valores por defecto del agente. Son preferencias tuyas; se aplican a partir de tu siguiente mensaje.",
    activar: "Aplicar mis preferencias al agente",
    disclaimer:
      "Esto orienta al agente (steering); no son reglas absolutas — no cambian definiciones de métricas ni la seguridad.",
    nombre: "Nombre",
    nombrePlaceholder: "¿Cómo quieres que te llame el agente?",
    sectionPresentation: "Presentación e interacción",
    sectionNotes: "Notas",
    tono: "Tono",
    formato: "Formato de respuesta",
    longitud: "Longitud de respuesta",
    desambiguacion: "Desambiguación antes de ejecutar",
    notas: "Notas libres",
    notasPlaceholder: "Instrucciones ad-hoc para el agente…",
    save: "Guardar",
    saving: "Guardando…",
    saved: "Preferencias guardadas · se aplican en tu siguiente mensaje.",
    reset: "Restablecer",
    cancel: "Cerrar",
    loadError: "No se pudieron cargar tus preferencias.",
    saveError: "No se pudieron guardar tus preferencias.",
    none: "Sin preferencia",
  },
  errors: {
    generic: "Lo siento, ocurrió un error al procesar tu solicitud. Por favor, inténtalo de nuevo.",
    configPrefix: "Error de configuración",
    responsePrefix: "No se pudo obtener respuesta",
    feedbackPrefix: "No se pudo enviar el comentario",
    authRequired: "Se requiere autenticación. Por favor, inicia sesión de nuevo.",
    // Known issue: toolResult/toolUse mismatch in session history.
    // https://github.com/strands-agents/harness-sdk/issues/1610
    conversationCorrupted:
      "Hubo un problema con esta conversación. Por favor inicia una nueva con el botón + del panel izquierdo.",
    // Adjunto que el runtime no pudo usar. El turno NO falló: la respuesta llegó,
    // pero sin el archivo. Antes esto era solo un `logger.warning` del servidor y el
    // usuario leía cifras que ignoraban su documento sin saberlo.
    attachmentRejectedPrefix: "Adjunto no procesado",
    attachmentUploadFailed:
      "No se pudo subir el archivo. La respuesta no lo toma en cuenta.",
  },
} as const
